import { useState } from 'react';
import { Search, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAdminAudit } from '@/features/admin/useAdmin';

const SEVERITIES: { id: number; label: string }[] = [
  { id: 0, label: 'Todos' },
  { id: 10, label: 'Info+' },
  { id: 20, label: 'Warning+' },
  { id: 30, label: 'Critical' },
];

export function AuditTab() {
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [sev, setSev] = useState(0);
  const auditQ = useAdminAudit({ limit: 200, action, actor_email: actor, severity_gte: sev });
  const rows = auditQ.data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Auditoria
        </h2>
        <span className="font-mono tabular text-xs" style={{ color: 'var(--text-muted)' }}>
          {rows.length}
        </span>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={12} strokeWidth={1.8} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            value={action}
            onChange={(e) => setAction(e.currentTarget.value)}
            placeholder="Accion (ej. user.update)"
            className="h-9 w-56 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] pl-7 pr-2 font-mono tabular text-xs focus:border-[var(--accent)] focus:outline-none"
            style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
          />
        </div>
        <input
          value={actor}
          onChange={(e) => setActor(e.currentTarget.value)}
          placeholder="Email actor"
          className="h-9 w-56 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-xs focus:border-[var(--accent)] focus:outline-none"
          style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
        />
        <div
          className="inline-flex overflow-hidden rounded-[var(--radius-sm)] border"
          style={{ borderColor: 'var(--border-default)' }}
        >
          {SEVERITIES.map((s) => (
            <button
              key={s.id}
              onClick={() => setSev(s.id)}
              className="h-9 px-3 text-xs"
              style={{
                background: sev === s.id ? 'var(--accent)' : 'transparent',
                color: sev === s.id ? 'var(--surface-base)' : 'var(--text-secondary)',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-[var(--radius-md)] border" style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
        <table className="w-full text-left text-sm">
          <thead style={{ background: 'var(--surface-2)' }}>
            <tr className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              <Th>Fecha</Th>
              <Th>Actor</Th>
              <Th>Accion</Th>
              <Th>Resumen</Th>
              <Th>IP</Th>
              <Th>Estado</Th>
            </tr>
          </thead>
          <tbody>
            {auditQ.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="mr-2 inline animate-spin" /> Cargando log...
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <Td>
                  <span className="font-mono tabular text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </Td>
                <Td>
                  <span className="font-mono tabular text-[11px]">{r.actor_email || '-'}</span>
                </Td>
                <Td>
                  <span className="font-mono tabular text-[11px]" style={{ color: 'var(--accent)' }}>
                    {r.action}
                  </span>
                </Td>
                <Td>
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {r.summary}
                  </span>
                </Td>
                <Td>
                  <span className="font-mono tabular text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    {r.ip || '-'}
                  </span>
                </Td>
                <Td>
                  {r.status === 'ok' ? (
                    <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--success)' }}>
                      <CheckCircle2 size={11} strokeWidth={2} /> ok
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--danger)' }}>
                      <AlertTriangle size={11} strokeWidth={2} /> {r.status}
                    </span>
                  )}
                </Td>
              </tr>
            ))}
            {!auditQ.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                  Sin eventos.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 ${className}`}>{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 ${className}`}>{children}</td>;
}
